import { useEffect, useMemo, useState } from "react";
import type { Answer, Question as QuestionT } from "../types";
import { useAnswers } from "../state/AnswersContext";
import { useRequiredFields } from "../state/RequiredFields";
import { stripHtml } from "../util/text";
import { yesNoOrder } from "../util/yesNo";
import { isHidden, evalRule } from "../util/rule";
import { normalizeHeatDutySuffix, isSuffixNormalized } from "../util/numericSuffix";
import { comboOptions, resolveTyped } from "../util/comboOptions";
import { dropdownTokenMap } from "../util/mappingDecoder";
import { directionOneKeysToClear } from "../util/fromCenter";
import { createCustomAnswer, mergeCustomAnswers, registerTokens } from "../util/customAnswers";
import { getLaunchContext } from "../util/launchContext";
import { getProfile } from "../products";
import { sizeOverLimit } from "../util/sizeLimits";
import { MIRRORED_TITLE } from "../util/prediction";
import { PredictedInput, PredictedTextarea } from "./PredictedText";
import ComboBox from "./ComboBox";
import TableWidget from "./TableWidget";
import CalculateButton from "./CalculateButton";

// Free-text fields that a reactive operation ALSO auto-writes, guarded by a
// "memory" shadow answer: the op only overwrites the field while its value still
// equals that shadow (see the Orifice Sizing op). A manual edit must therefore
// desync the shadow so the op treats it as a user override and stops rewriting;
// clearing the field (empty) re-syncs and re-enables auto-calc. Maps the edited
// question code -> [shadow question, shadow answer].
const OVERRIDE_MEMORY_FIELDS: Record<string, [string, string]> = {
  MIXER_ORIFICE_SIZE: ["MIXERORIFICE_MEMORY", "MIXERORIFICE_MEMORY"],
};

type Props = { question: QuestionT };

export default function Question({ question }: Props) {
  const { answers: state, invalidFields, product } = useAnswers();
  // Which dropdowns are typeable is a per-product decision; renderWidget is a plain
  // function and cannot read the context itself.
  const isCombo = getProfile(product).comboBoxQuestionCodes.has(
    question.custrecord_cpqc_question_code
  );
  const label =
    stripHtml(question.custrecord_cpqc_question_label_html) || question.name;
  const code = question.custrecord_cpqc_question_code;
  // The UP inlet row doubles as the firetube "Direction" control: when any inlet
  // is firetube-oriented, Up/Down dropdowns appear in its header (see
  // TableWidget) and the section reads "Direction" instead of "UP".
  // From-Center section mode (one radio next to the Orientation title). When on,
  // the UP row reads "Direction 2" (Up/Down) and the IN row "Direction 1 /
  // Reference to Center" — for every inlet. Selecting Firetube auto-enables this
  // mode (reactive op), so Firetube is no longer a separate trigger. See the
  // ORIENTATION_TABLE radio (FromCenterRadio).
  const fromCenterMode = state["FROM_CENTER_MODE/ON"] === true;
  const displayLabel =
    code === "UP" && fromCenterMode
      ? "Direction 2"
      : code === "IN" && fromCenterMode
        ? "Direction 1 / Reference to Center"
        : label;
  // Never required (Required Heat Duty, Fuel Pressure, and any other code the product
  // marks optional). The same set validation skips, so a field cannot be labelled
  // optional and still block Save & Submit.
  const optionalHere = getProfile(product).optionalQuestionCodes.has(code);
  const isInvalid = invalidFields.has(code);
  // Required right now and still blank: a dot on the label, so the "N to go" on the progress
  // strip can be found on the page; the NEXT one (the strip's "Next:") also gets the amber
  // edge, so one field stands out rather than a whole new form. Once Save & Submit flags a
  // field, the red invalid look takes over.
  const required = useRequiredFields();
  const isPending = required.pending.has(code) && !isInvalid;
  const isNext = isPending && required.next?.code === code;
  const spans = Number(question.custrecord_cpqc_question_spans || 12);
  const qType = question.custrecord_cpqc_question_type;

  // CPQ READ-ONLY RULE (question level). Distinct from a hide rule: the field
  // stays on screen showing its value, but cannot be edited. Use it where the
  // value is still worth reading even though the configuration decides it — e.g.
  // Bolt Hole Size with Welded Nuts selected, which used to hide outright.
  //
  // NOTE the field name: questions carry `parsed_ro_rule` (QTable COLUMN answers
  // carry `parsed_ronly_rule`, evaluated separately in TableWidget). Getting that
  // wrong reads undefined and silently never locks.
  //
  // A locked field is NOT hidden, so its answer still encodes into the mapping
  // and transfers to the item — which is the point of showing it.
  const locked = evalRule(
    String(question.custrecord_cpqc_question_parsed_ro_rule ?? ""),
    state
  );

  const orderedAnswers = useMemo(
    () =>
      [...question._answers]
        .filter(
          (a) =>
            !isHidden(
              {
                hideRule: a.custrecord_cpqc_answer_hide_rule,
                parsedHideRule: a.custrecord_cpqc_answer_parsed_hide_rule,
              },
              state
            )
        )
        .sort(
          (a, b) =>
            Number(a.custrecord_cpqc_answer_sequence || 0) -
            Number(b.custrecord_cpqc_answer_sequence || 0)
        ),
    [question, state]
  );

  if (qType === "10") {
    return (
      <div
        className="cpq-spacer"
        style={{ gridColumn: `span ${Math.max(1, Math.min(12, spans))}` }}
        aria-hidden
      />
    );
  }



  return (
    <div
      className={`cpq-question${isInvalid ? " is-invalid" : ""}${isPending ? " is-pending" : ""}${isNext ? " is-next" : ""}${locked ? " is-locked" : ""}`}
      data-code={code}
      data-type={qType}
      style={{ gridColumn: `span ${Math.max(1, Math.min(12, spans))}` }}
      title={locked ? "Set by the configuration — shown for reference, not editable" : undefined}
    >
      <label className="cpq-question-label">
        {displayLabel || code}
        {optionalHere && <span className="cpq-question-optional"> (optional)</span>}
        {code === "ORIENTATION_TABLE" && <FromCenterRadio />}
      </label>
      {renderWidget(question, orderedAnswers, locked, isCombo)}
    </div>
  );
}

// One radio next to the "Orientation" title that toggles the section-wide
// From-Center mode. A lone radio can't self-uncheck, so we handle onClick to
// toggle the controlled state (readOnly silences React's no-onChange warning).
// Stored at answers["FROM_CENTER_MODE/ON"]; the mapping encoder reads it to
// encode the From Center (Left/Right) + Direction 1 (In/Reference Center) slots
// for non-firetube inlets. Round-trips via the config snapshot.
function FromCenterRadio() {
  const { answers, setAnswer } = useAnswers();
  const on = answers["FROM_CENTER_MODE/ON"] === true;
  return (
    <label
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        marginLeft: 10,
        fontSize: 12,
        fontWeight: 500,
        cursor: "pointer",
        verticalAlign: "middle",
      }}
      title="Configure non-firetube inlets from the center (Direction 1 / Reference to Center)"
      onClick={(e) => {
        e.preventDefault();
        // SWITCHING OFF CLEARS DIRECTION 1. Kevin, 2026-09-02: "they should clear."
        //
        // From-Center mode is what REVEALS the IN and FROM_CENTER rows (see Group.tsx --
        // NetSuite hides both unless an inlet is Firetube). Switching it off therefore hides
        // them again while their values stay in the model, and the description operation
        // reads the model with no notion of visibility: qtVal() suppresses blank and None
        // and nothing else. HCL-SB26/18-12-3 printed `4" In` on Main and Option 3 that way
        // -- the same 4" that had been entered as `4" Right of Center`, silently re-datumed
        // from the firetube centre to the edge by dropping FROM_CENTER. Same number,
        // different hole.
        //
        // Which keys those are lives in util/fromCenter.ts, where it is tested:
        // npm run test:fromcenter.
        //
        // Only on the way OFF. Turning it ON also changes what an existing distance means,
        // but it SHOWS the rows while doing so, so the value is in front of the person who
        // can correct it. Clearing there would throw away typed work to prevent a
        // misreading the operator can already see.
        if (on) {
          for (const [q, a] of directionOneKeysToClear(answers)) setAnswer(q, a, null);
        }
        setAnswer("FROM_CENTER_MODE", "ON", on ? null : true);
      }}
    >
      <input type="radio" checked={on} readOnly tabIndex={0} />
      From Center
    </label>
  );
}

// `locked` comes from the question's read-only rule. Only the editable widgets
// take it; TableWidget evaluates its columns' own per-answer ronly rules, and
// CalculateButton/spacers have nothing to lock.
function renderWidget(
  question: QuestionT,
  answers: Answer[],
  locked = false,
  isCombo = false
) {
  const qType = question.custrecord_cpqc_question_type;

  if (answers.length === 0 && qType !== "12") {
    return (
      <div className="cpq-question-empty">
        No options match the current selections. (Some questions depend on
        other answers — try selecting related fields above first.)
      </div>
    );
  }

  switch (qType) {
    case "3":
      return <CheckboxWidget question={question} answers={answers} locked={locked} />;
    case "4":
      return isCombo ? (
        <ComboSelectWidget question={question} answers={answers} locked={locked} />
      ) : (
        <SelectWidget question={question} answers={answers} locked={locked} />
      );
    case "7":
      return <TextWidget question={question} answers={answers} locked={locked} />;
    case "11":
      return <TableWidget question={question} />;
    case "12":
      return <CalculateButton question={question} />;
    default:
      return (
        <div className="cpq-question-unknown">
          [unsupported widget type={qType}, {answers.length} answers]
        </div>
      );
  }
}

function TextWidget({
  question,
  answers,
  locked = false,
}: {
  question: QuestionT;
  answers: Answer[];
  locked?: boolean;
}) {
  const ans = answers[0];
  const qCode = question.custrecord_cpqc_question_code;
  const aCode = ans.custrecord_cpqc_answer_code;
  const key = `${qCode}/${aCode}`;
  const { answers: state, setAnswer, product, isPredicted, predictionNote } = useAnswers();
  const val = (state[key] as string | number | undefined) ?? "";
  // A predicted value shows grey; typing over it starts a new value (see util/prediction).
  const predicted = !locked && isPredicted(key);
  const note = predicted ? predictionNote(key) : null;
  const isArea = ans.custrecord_cpqc_answer_add_textarea;
  // datatype=5 is "numeric-like" but in practice can hold fractional strings
  // (e.g. MIXER_ORIFICE_SIZE = "7/16"). Always render as text input with a
  // numeric-keyboard hint; <input type="number"> would refuse to display "7/16".
  const isNumericHint = ans.custrecord_cpqc_answer_datatype === "5";

  // A field emptied WHILE STILL BEING EDITED is not committed as empty until the user leaves
  // it. To an operation, empty means "work it out for me": Orifice Sizing re-fills a blank
  // Mixer Orifice Size and back-fills a blank Fuel Pressure (the documented way back to
  // auto-calc). Committed on the keystroke, that ran 150 ms after select-all + Backspace, so
  // the next keystrokes landed after the re-filled value -- "21/64" + "3/8" = "21/643/8", "8"
  // + "9" = "89" -- and the field looked like it would not change (Kevin, 2026-10-22). Typing
  // over a selection never passes through empty, which is why it only bit some edits.
  const [heldEmpty, setHeldEmpty] = useState(false);

  // Commit a value: set it (thousands separators dropped on a suffix-normalized field, see
  // below), and -- for an auto-written field -- desync its memory shadow so the reactive op
  // treats this as a manual override and stops overwriting it. A non-empty edit keeps
  // `currentSize !== memory` true in the op; an empty value clears the shadow too,
  // re-enabling auto-calc. The single-line input used to skip the shadow (it set the answer
  // directly), so a typed Mixer Orifice Size was not marked as the user's.
  const commit = (raw: string) => {
    const v = isSuffixNormalized(product, qCode) ? raw.replace(/[,\s]/g, "") : raw;
    setAnswer(qCode, aCode, v);
    const mem = OVERRIDE_MEMORY_FIELDS[qCode];
    if (mem) setAnswer(mem[0], mem[1], v.trim() === "" ? "" : "__user__");
  };
  const handleChange = (raw: string) => {
    if (raw.trim() === "" && String(val).trim() !== "") {
      setHeldEmpty(true);
      return;
    }
    setHeldEmpty(false);
    commit(raw);
  };

  // On blur: an emptied field is committed empty now, and engineering suffixes are
  // normalized (e.g. "3M"/"3MM" → "3000000") for fields that use them, so the committed
  // answer is a clean number — matching what the CPQ sizing scripts expect to read.
  const onCommit = (raw: string) => {
    if (heldEmpty) {
      setHeldEmpty(false);
      if (raw.trim() === "") commit("");
      return;
    }
    if (!isSuffixNormalized(product, qCode)) return;
    const normalized = normalizeHeatDutySuffix(raw);
    if (normalized !== raw) setAnswer(qCode, aCode, normalized);
  };
  const shown = heldEmpty ? "" : String(val);

  if (isArea) {
    return (
      <PredictedTextarea
        className={`cpq-input${predicted ? " is-predicted" : ""}${note?.unsure ? " is-unsure" : ""}`}
        title={note?.title}
        predicted={predicted}
        value={shown}
        rows={3}
        disabled={locked}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={(e) => onCommit(e.target.value)}
      />
    );
  }
  // A size over its product's ceiling (Cell OD > 99.99") is flagged as it is typed; Save
  // and Submit refuses it outright (see util/sizeLimits).
  const overMax = sizeOverLimit(product, key, val);
  return (
    <>
      <PredictedInput
        type="text"
        inputMode={isNumericHint ? "decimal" : "text"}
        className={`cpq-input${overMax != null ? " cpq-input-invalid" : ""}${predicted ? " is-predicted" : ""}${note?.unsure ? " is-unsure" : ""}`}
        title={note?.title}
        aria-invalid={overMax != null}
        predicted={predicted}
        value={shown}
        disabled={locked}
        // Drop thousands separators AS TYPED on a suffix-normalized field (commit), so a
        // comma'd value is never committed even for the keystrokes before blur — the
        // hide rules run on every edit, and one comma empties the Main Assembly Size
        // list (Number("3,500,000") is NaN, so every <= threshold fails). Pasting
        // "3,500,000" lands as 3500000. Whitespace goes too, for the "3 500 000"
        // spelling; "3 MM" therefore shows as "3MM" while typing and still resolves
        // to 3000000 on blur, where the M/MM suffix is handled (it can only be read
        // once they have stopped typing).
        onChange={(e) => handleChange(e.target.value)}
        onBlur={(e) => onCommit(e.target.value)}
      />
      {overMax != null ? <div className="cpq-question-note">Max {overMax}"</div> : null}
    </>
  );
}

// CPQ question type 3 = checkbox. Natural Draft Burners has none, so this arrived
// with Flame Cells (Core Protruding, Custom Core Length, Natural Draft Flow, Ship
// Star Bars loose) — before it existed those four rendered as
// "[unsupported widget type=3]".
//
// RENDERED AS AN EXPLICIT YES/NO PAIR, not a tickbox (Kevin, 2026-08-18: "you should make
// any check box show a yes and no"). A single tickbox cannot show the state these questions
// actually have. They hold THREE: Yes, No, and unanswered — and a tickbox draws the third
// one identically to No, so the form said "No" for a question nobody had touched.
//
// That is not cosmetic. Operation 13 needed this to read
//     !customcorelength || customcorelength === 'CUSTOM_CORE_LENGTH_NO'
// because an untouched Custom Core Length is unanswered rather than No, and the earlier code
// that only checked for the No CODE silently took the wrong branch — the description printed
// the internal plugged length (3-15/16") where it should have printed the finished 4". The
// fix there was to treat unanswered as No; the fix HERE is to stop drawing a state the user
// never chose. With two radios, neither is filled until they pick, so unanswered looks
// unanswered.
//
// The STATE SHAPE IS A SELECT'S, not a boolean. A type-3 question carries two real
// answers, Yes and No, and the operations read them exactly like any dropdown:
// `setAnswer('CORE_PROTRUDING', 'CORE_PROTRUDING_NO')` and
// `getAnswerCode('CORE_PROTRUDING')`. So ticking the box must set the Yes answer true
// and clear No, NOT write `true` under the question code — an op comparing
// getAnswerName(...) === 'No' has to keep working.
//
// Yes/No is identified by the answer's `name`, NOT by its code: the codes are not
// consistently derived from the question code (NATURAL_DRAFT_FLOW's answers are
// NATURAL_DRAFT_YES / NATURAL_DRAFT_NO), so a `${qCode}_YES` guess would silently
// fail on that one. Code contains YES/NO is kept as a fallback.
function CheckboxWidget({
  question,
  answers,
  locked = false,
}: {
  question: QuestionT;
  answers: Answer[];
  locked?: boolean;
}) {
  const qCode = question.custrecord_cpqc_question_code;
  const { answers: state, setAnswer, isPredicted, isMirrored, predictionNote } = useAnswers();
  const chosen = answers.find((a) => state[`${qCode}/${a.custrecord_cpqc_answer_code}`] === true);
  const predicted = !locked && !!chosen && isPredicted(`${qCode}/${chosen.custrecord_cpqc_answer_code}`);
  const note = predicted ? predictionNote(`${qCode}/${chosen!.custrecord_cpqc_answer_code}`) : null;
  // ...and swapped from the other burner by the MB mirror: marked for the user to check.
  const mirrored = predicted && isMirrored(`${qCode}/${chosen!.custrecord_cpqc_answer_code}`);

  // Yes first, then No — see src/util/yesNo.ts, which is shared with test:yesno so the
  // classification cannot drift away from what the real question data needs.
  const options = yesNoOrder(answers);

  const pick = (code: string) => {
    if (locked) return;
    // Clear EVERY answer, not just the visible ones — same reason SelectWidget does: an
    // option hidden by a rule at pick time would otherwise stay true and leave the question
    // with two true answers, which then trips rules keyed on the stale one.
    for (const a of question._answers) {
      const c = a.custrecord_cpqc_answer_code;
      setAnswer(qCode, c, c === code ? true : null);
    }
  };

  // No answers at all: say so rather than draw a control that cannot store anything, which
  // would read as "answered No".
  if (options.length === 0) {
    return <div className="cpq-question-empty">This checkbox has no answers defined.</div>;
  }

  return (
    <div
      className={`cpq-radio-group${locked ? " is-locked" : ""}${predicted ? " is-predicted" : ""}${mirrored ? " is-mirrored" : ""}${note?.unsure ? " is-unsure" : ""}`}
      title={mirrored ? MIRRORED_TITLE : note?.title}
    >
      {options.map((a) => {
        const code = a.custrecord_cpqc_answer_code;
        const labelText = stripHtml(a.custrecord_cpqc_answer_label_html) || a.name || code;
        return (
          <label
            key={a.id}
            className={`cpq-radio-option${locked ? " is-locked" : ""}`}
          >
            <input
              type="radio"
              // Scoped to the question, so the two options are mutually exclusive in the
              // browser as well as in the state.
              name={`cpq-${qCode}`}
              checked={state[`${qCode}/${code}`] === true}
              disabled={locked}
              onChange={() => pick(code)}
            />
            <span>{labelText}</span>
          </label>
        );
      })}
    </div>
  );
}

/** A dropdown that can also be TYPED into. Used where the list is long enough that
 *  scrolling it is the slow way to answer. Which questions get this is declared per product
 *  (a question code is only unique within one CPQ), not hardcoded here.
 *
 *  Typing is forgiving: 24, 24", 19 3/4, 19-3/4 and 19.75 all land on the right option,
 *  because matching runs through the encoder's own normalizeMixedInches. Anything that does
 *  NOT resolve leaves the question UNANSWERED and says so inline. That matters more than it
 *  looks: an unanswered slot encodes as its null token, so a silently-accepted value would
 *  disappear from the item with no error anywhere.
 *
 *  WHY THIS IS NOT A NATIVE <datalist>. It was, and the native behaviour broke editing in
 *  two ways Kevin hit immediately:
 *    - The text was rewritten to the canonical label on every keystroke that resolved. So
 *      deleting the trailing " from `81"` left `81`, which still matched, which put the "
 *      straight back - the value could only be edited from the middle, never the end.
 *    - Once the box held an exact option, the browser filtered the list down to that one
 *      entry, so the dropdown could not be reopened without clearing the value first.
 *  Both come from the browser owning the list. This owns it instead: the text is never
 *  rewritten mid-edit (only on blur), and the caret always opens the full list. */
function ComboSelectWidget({
  question,
  answers,
  locked = false,
}: {
  question: QuestionT;
  answers: Answer[];
  locked?: boolean;
}) {
  const qCode = question.custrecord_cpqc_question_code;
  const { answers: state, setAnswer, product, mappingSpec, isPredicted } = useAnswers();
  const options = useMemo(() => comboOptions(answers), [answers]);
  const canCreate = getProfile(product).createsAnswersFor.has(qCode);
  const autoCreate = canCreate && getProfile(product).autoCreatesAnswersFor.has(qCode);
  const sizeRules = useMemo(
    () => ({ wholeNumbers: getProfile(product).wholeNumberQuestionCodes.has(qCode) }),
    [product, qCode]
  );

  // Where a size that is in NO list goes, for a question whose list can no longer produce an
  // encodable id (Flange to Nozzle End -> EXT_NIP). null for every other question, which is
  // what keeps the create/refuse behaviour below untouched. See unlistedSizeTextFallback.
  const textFallback = getProfile(product).unlistedSizeTextFallback.get(qCode) ?? null;
  // The value is held BARE ("31-1/2") because that is the form the mapping slot encodes and
  // the item's free-text field carries; the inch mark is added only for display, so the box
  // reads like its listed neighbours.
  const fallbackValue = textFallback ? String(state[`${textFallback[0]}/${textFallback[1]}`] ?? "").trim() : "";
  const setFallback = (bare: string) => {
    if (textFallback) setAnswer(textFallback[0], textFallback[1], bare);
  };

  // A dropdown slot carries its token map INLINE. The top-level TOKEN_MAPS is for QTable
  // slots only, and reading dropdown tokens from there returns nothing for EVERY question --
  // which reads exactly like "no option has a token", and would empty this dropdown
  // completely.
  const tokenMap = useMemo(() => dropdownTokenMap(mappingSpec, qCode), [mappingSpec, qCode]);

  // Can this answer code reach the item? The MAPPING slot's token map is the only route out
  // of cpq: an option missing from it encodes the slot's NULL token, so the size silently
  // vanishes from the item.
  //
  // Not hypothetical for Hood Size. questions.json offers 6", 8", 10" and 12" and the HOODSIZE
  // token map has none of them -- nor does CUSTOMLISTHOOD_SIZE, which starts at 14". Picking
  // one looked completely normal and shipped a burner with no hood size at all.
  const hasToken = (code: string): boolean => {
    if (!code) return true; // nothing selected: nothing to check
    const extra = (globalThis as unknown as { __CPQ_EXTRA_TOKENS?: Record<string, Record<string, string>> })
      .__CPQ_EXTRA_TOKENS;
    if (extra?.[qCode]?.[code]) return true;
    // No slot for this question at all means it is not mapped, so there is nothing to be
    // missing from -- do not treat that as a gap.
    if (!tokenMap) return true;
    return Object.prototype.hasOwnProperty.call(tokenMap, code);
  };

  // The dropdown offers only sizes that NetSuite actually has. Two separate exclusions:
  //
  //   o.hidden    real list values nobody here orders. Flange to Nozzle End went from 32
  //               entries to 81 when the whole list was loaded; the extra 49 are matched by
  //               typing but not scrolled past by everyone.
  //   !hasToken   options cpq offers that NetSuite does not have -- Hood Size 6", 8", 10"
  //               and 12". Kevin, 2026-08-20: "can we not offer 6,8,10,12 on cpq because it
  //               is not in netsuite. now if it does get added later then thats fine."
  //
  // "Later" needs no code change: a size gains a token the moment it is recorded (typing it
  // creates it, and registerTokens publishes the id), so it starts being offered from then
  // on. Nothing is removed from questions.json, so a saved configuration that already holds
  // one of the four still opens showing it -- the box displays the selected label whether or
  // not it is in the list.
  //
  // commit() still resolves against the FULL option set, so any of these remains typeable.
  const listedOptions = useMemo(
    () =>
      options
        .filter((o) => !o.hidden && hasToken(o.code))
        .map((o) => ({ value: o.label, label: o.label })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options, tokenMap, qCode]
  );

  const selectedCode =
    answers.find((a) => state[`${qCode}/${a.custrecord_cpqc_answer_code}`] === true)
      ?.custrecord_cpqc_answer_code ?? "";
  const selectedLabel = options.find((o) => o.code === selectedCode)?.label ?? "";
  // What the box shows. A listed option wins: commit() clears the fallback whenever one is
  // chosen, so the two hold a value only in the moment between those two state writes -- and
  // reading the option first means a saved configuration on a listed size never displays a
  // leftover. With nothing selected, a held free-text size is displayed rather than an empty
  // box, which is how a loaded config (or an Item Template) shows `31-1/2"` back.
  const shownLabel = selectedLabel || (fallbackValue ? `${fallbackValue}"` : "");
  // Grey while the shown value is still the prediction (a listed option, or the free-text size).
  const predicted =
    !locked &&
    (selectedCode
      ? isPredicted(`${qCode}/${selectedCode}`)
      : !!textFallback && !!fallbackValue && isPredicted(`${textFallback[0]}/${textFallback[1]}`));

  const [text, setText] = useState(shownLabel);
  const [problem, setProblem] = useState("");
  const [busy, setBusy] = useState("");
  // A size that exists NOWHERE is not created on the spot: it waits here until the user
  // confirms. The question stays UNANSWERED meanwhile, which is the honest state - nothing
  // has been chosen yet. Only ever set for a size nobody has used before; picking or typing
  // one of the real sizes never reaches this.
  const [pendingNew, setPendingNew] = useState("");

  // A reactive operation can change the answer underneath us, and loading a saved
  // configuration sets it from outside entirely, so the box follows the selection whenever
  // it moves for any reason other than typing in it.
  useEffect(() => {
    setText(shownLabel);
    setProblem("");
    setPendingNew("");
  }, [shownLabel]);

  // Clear EVERY option, not just the visible ones, for the same reason SelectWidget does:
  // an option hidden by a hide rule at pick time would otherwise stay true.
  const select = (aCode: string | null) => {
    for (const a of question._answers) {
      const code = a.custrecord_cpqc_answer_code;
      setAnswer(qCode, code, code === aCode ? true : null);
    }
  };

  // Record an option so it is LISTED from now on: either a size that exists but was not
  // previously offered, or one that exists nowhere and has to be created. Both go through
  // the same endpoint, which is idempotent - an existing NetSuite value comes back with its
  // own id and created:false.
  const recordAndSelect = async (label: string, code: string | null) => {
    setBusy(`Adding ${label}…`);
    setProblem("");
    const res = await createCustomAnswer({
      product,
      questionCode: qCode,
      label,
      userId: getLaunchContext().userId ?? undefined,
      userName: getLaunchContext().userName ?? undefined,
    });
    setBusy("");
    if (!res.ok) {
      // An option that already exists is still selectable even if RECORDING it failed - the
      // only loss is that it stays unlisted. A brand-new one has nothing to select, so the
      // question stays unanswered rather than holding a value the item cannot carry.
      if (code) {
        setText(label);
        select(code);
        setProblem(`Selected ${label}, but it could not be added to the list: ${res.reason}`);
      } else {
        if (selectedCode) select(null);
        setProblem(`Could not add ${label}: ${res.reason}`);
      }
      return;
    }
    // Token first. If the option were added to the question before the encoder knew its
    // token, a submit in that window would encode the null token and lose the size.
    registerTokens([res.answer]);
    mergeCustomAnswers([question], [res.answer]);
    setText(res.answer.label);
    select(res.answer.answerCode);
  };

  const commit = (raw: string) => {
    const r = resolveTyped(raw, options, sizeRules);
    if (r.kind === "match") {
      setProblem("");
      setText(r.option.label);
      // A listed size is carried by the LIST answer, so any free-text size held from an
      // earlier edit has to go. Leaving it would put two lengths on one item and, because the
      // text question feeds the fingerprint, would move the part number of a configuration
      // that is on a perfectly ordinary listed size -- i.e. build a duplicate item for it.
      if (fallbackValue) setFallback("");
      if (r.option.code !== selectedCode) select(r.option.code);
      // Using an unlisted-but-real size lists it for everyone from now on -- and so does
      // using a listed one that has no token yet, which is the only way an option that
      // cannot reach the item ever gets fixed.
      if (r.option.hidden || !hasToken(r.option.code)) {
        void recordAndSelect(r.option.label, r.option.code);
      }
      return;
    }
    if (r.kind === "empty") {
      setProblem("");
      if (selectedCode) select(null);
      if (fallbackValue) setFallback("");
      return;
    }
    // A real size that no list holds, on a question that keeps such sizes as free text -- only
    // where the list cannot take it (none today: Flange to Nozzle End creates again since
    // 2026-10-19, its slot having widened). Taken as typed: nothing is created in NetSuite.
    if (r.kind === "unlisted" && textFallback && !canCreate) {
      setProblem("");
      setText(r.label);
      if (selectedCode) select(null);
      if (r.key !== fallbackValue) setFallback(r.key);
      return;
    }
    if (r.kind === "unlisted" && canCreate) {
      if (autoCreate) {
        // Create it outright. Only for questions whose grammar makes a near-miss typo
        // impossible to write (whole inches), which is what a confirmation step would
        // otherwise be there to catch.
        void recordAndSelect(r.label, null);
        return;
      }
      // Propose, do not create. Adding a value to the NetSuite list is permanent - values
      // can be inactivated, not deleted - so a typo should cost a glance, not a cleanup.
      if (selectedCode) select(null);
      setPendingNew(r.label);
      return;
    }
    // Unresolved: leave the question unanswered and say why. The held free-text size goes too,
    // so what the box reports and what the item would carry cannot disagree.
    if (selectedCode) select(null);
    if (fallbackValue) setFallback("");
    setProblem(
      r.kind === "unlisted"
        ? `${r.label} is not one of the listed sizes.`
        : sizeRules.wholeNumbers
          ? `"${r.typed}" is not a whole number of inches.`
          : `"${r.typed}" is not a size.`
    );
  };

  return (
    <>
      <ComboBox
        value={text}
        options={listedOptions}
        disabled={locked || busy !== ""}
        inputClassName={`${problem ? "cpq-input-invalid" : ""}${predicted ? " is-predicted" : ""}`.trim()}
        replaceOnType={predicted}
        onChange={(v) => {
          setText(v);
          setProblem("");
          setPendingNew("");
        }}
        onCommit={commit}
      />
      {pendingNew ? (
        <div className="cpq-question-note cpq-combo-confirm">
          <span>{pendingNew} is not in the list yet.</span>
          <button
            type="button"
            className="cpq-combo-add"
            // mousedown+preventDefault keeps focus on the input, so no blur fires and
            // re-commits the same text underneath the click.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              const label = pendingNew;
              setPendingNew("");
              void recordAndSelect(label, null);
            }}
          >
            Add {pendingNew}
          </button>
        </div>
      ) : null}
      {busy ? <div className="cpq-question-note">{busy}</div> : null}
      {problem ? <div className="cpq-question-note">{problem}</div> : null}
    </>
  );
}

function SelectWidget({
  question,
  answers,
  locked = false,
}: {
  question: QuestionT;
  answers: Answer[];
  locked?: boolean;
}) {
  const qCode = question.custrecord_cpqc_question_code;
  const { answers: state, setAnswer, isPredicted, isMirrored, predictionNote } = useAnswers();

  const selectedCode =
    answers.find(
      (a) => state[`${qCode}/${a.custrecord_cpqc_answer_code}`] === true
    )?.custrecord_cpqc_answer_code ?? "";
  // Preselected by the prediction: grey until a different answer is picked.
  const predicted = !locked && selectedCode !== "" && isPredicted(`${qCode}/${selectedCode}`);
  const mirrored = predicted && isMirrored(`${qCode}/${selectedCode}`);
  const note = predicted ? predictionNote(`${qCode}/${selectedCode}`) : null;

  const onChange = (aCode: string) => {
    // Clear EVERY option (question._answers), not just the currently-visible
    // ones. If we only cleared the visible list, an option hidden by a hide rule
    // at pick-time stays true, leaving the select with two true answers. That
    // stale value then trips rules keyed on it — e.g. a lingering
    // OFFSET_1_SIDE/OFFSET_NONE after picking "Up" makes SET_OFFSET_DISTANCE_0
    // force the offset distance back to 0 on every reactive run.
    for (const a of question._answers) {
      const code = a.custrecord_cpqc_answer_code;
      setAnswer(qCode, code, code === aCode ? true : null);
    }
  };

  return (
    <select
      className={`cpq-input cpq-select${predicted ? " is-predicted" : ""}${mirrored ? " is-mirrored" : ""}${note?.unsure ? " is-unsure" : ""}`}
      title={mirrored ? MIRRORED_TITLE : note?.title}
      value={selectedCode}
      disabled={locked}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="" hidden></option>
      {answers.map((a) => {
        const code = a.custrecord_cpqc_answer_code;
        const labelText =
          stripHtml(a.custrecord_cpqc_answer_label_html) || a.name || code;
        return (
          <option key={a.id} value={code}>
            {labelText}
          </option>
        );
      })}
    </select>
  );
}
